import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import { formatPrice, resolveImage, CATEGORY_LABELS } from "@/lib/format";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { Check, Download, Mail, ShieldCheck, Sparkles, ArrowLeft } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col bg-cream">
        <Header />
        <CartDrawer />
        <div className="max-w-3xl mx-auto px-5 py-24 text-center">
          <h1 className="font-serif-display text-4xl text-plum-deep">Product not found</h1>
          <Link to="/shop" className="inline-block mt-6 underline text-plum">Back to shop</Link>
        </div>
        <Footer />
      </div>
    );
  }
  if (!product) return <div className="p-10 text-center text-plum/60" data-testid="product-loading">Loading…</div>;

  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="product-detail-page">
      <Header />
      <CartDrawer />
      <section className="max-w-6xl mx-auto px-5 lg:px-10 pt-6 lg:pt-10 pb-20 w-full">
        <Link to="/shop" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-plum/60 hover:text-plum-deep">
          <ArrowLeft className="h-3 w-3" /> Back to shop
        </Link>
        <div className="mt-6 grid lg:grid-cols-2 gap-10 lg:gap-12">
          <div>
            <div className="aspect-[4/5] product-media max-h-[700px]">
              {product.image_url ? (
                <img src={resolveImage(product.image_url)} alt={product.title} data-testid="product-image" />
              ) : (
                <div className="h-full w-full ph-checker" />
              )}
            </div>
          </div>
          <div className="lg:pt-4">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold" data-testid="product-category">
              <Sparkles className="h-3 w-3" />
              {CATEGORY_LABELS[product.category] || product.category}
            </div>
            <h1 className="editorial font-serif-display text-3xl sm:text-5xl mt-4 leading-tight text-plum-deep" data-testid="product-title">
              {product.title}
            </h1>
            <div className="mt-6 text-2xl sm:text-3xl font-serif-display text-plum-deep" data-testid="product-price">
              {formatPrice(product.price, product.currency)}
            </div>
            <p className="mt-6 text-plum/80 whitespace-pre-wrap leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  add(product);
                  toast.success("Added to cart", { description: product.title });
                }}
                className="btn-primary justify-center sm:justify-start"
                data-testid="pdp-add-to-cart"
              >
                Add to cart · {formatPrice(product.price, product.currency)}
              </button>
              <Link
                to="/checkout"
                onClick={() => add(product)}
                className="btn-ghost-gold justify-center"
                data-testid="pdp-buy-now"
              >
                Buy now
              </Link>
            </div>

            <div className="mt-10 grid gap-3 text-sm text-plum/80">
              <div className="flex items-center gap-3"><Download className="h-4 w-4 text-gold" /> Instant download after purchase</div>
              <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-gold" /> Download link also emailed to you</div>
              <div className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-gold" /> Secure checkout by Stripe</div>
              <div className="flex items-center gap-3"><Check className="h-4 w-4 text-gold" /> Lifetime access to the file you buy</div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
